import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Footer from '../Footer';
import { resolveMediaUrl } from '../../utils/mediaUrl';
import GalleryTimeline from '../gallery/GalleryTimeline';
import GalleryToolbar from '../gallery/GalleryToolbar';
import { latestMonthKey, REMOVED_CATEGORY_IDS } from '../gallery/galleryFilters';
import { useGalleryFeed, fetchGalleryMeta, GALLERY_RENDER_BATCH } from '../gallery/useGalleryFeed';
import {
  anchorIdForMonth,
  buildGalleryGridRows,
  findStartIndexForMonth,
  scrollToMonthAnchor,
} from '../gallery/galleryScrollSync';
import polaroidImg from '../../img/intezmeny.png';
import './styles/GalleryPage.css';

function socialSourceLabel(source) {
  return source === 'facebook' ? 'Facebook' : 'Instagram';
}

function socialSourceBadgeClass(source) {
  return source === 'facebook' ? 'gallery-post-badge--facebook' : 'gallery-post-badge--instagram';
}

const SENTINEL_MARGIN_PX = 240;
const PUMP_MAX_ROUNDS = 12;

function isSentinelNearViewport(el) {
  if (!el) return false;
  const rect = el.getBoundingClientRect();
  return rect.top < window.innerHeight + SENTINEL_MARGIN_PX && rect.bottom > -SENTINEL_MARGIN_PX;
}

const GalleryPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryId, setCategoryId] = useState('all');
  const [activeMonthKey, setActiveMonthKey] = useState(null);
  const [monthSteps, setMonthSteps] = useState([]);
  const [specialMonths, setSpecialMonths] = useState([]);
  const [monthAnchors, setMonthAnchors] = useState([]);
  const [metaTotal, setMetaTotal] = useState(0);
  const [metaLoading, setMetaLoading] = useState(true);
  const [configured, setConfigured] = useState(false);
  const [isJumping, setIsJumping] = useState(false);

  const [selectedPost, setSelectedPost] = useState(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const sentinelRef = useRef(null);
  const isScrubbingRef = useRef(false);
  const jumpGenRef = useRef(0);
  const loadingRef = useRef(false);
  const isJumpingRef = useRef(false);

  const {
    items,
    visibleItems,
    total,
    hasMore,
    loading,
    loadingMore,
    canRenderMore,
    needsFetchMore,
    loadMoreStep,
    ensureLoadedThroughIndex,
    bumpRenderCount,
    renderCount,
  } = useGalleryFeed({
    categoryId,
    searchQuery,
    enabled: true,
  });

  const gridRows = useMemo(() => buildGalleryGridRows(visibleItems), [visibleItems]);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    isJumpingRef.current = isJumping;
  }, [isJumping]);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Időutazás a galériában | Napvirág Otthon';
    return () => {
      document.title = prevTitle;
    };
  }, []);

  const loadMeta = useCallback(async (cat, q) => {
    setMetaLoading(true);
    try {
      const data = await fetchGalleryMeta({ categoryId: cat, searchQuery: q });
      const steps = Array.isArray(data.monthSteps) ? data.monthSteps : [];
      setMonthSteps(steps);
      setSpecialMonths(Array.isArray(data.specialMonths) ? data.specialMonths : []);
      setMonthAnchors(Array.isArray(data.monthAnchors) ? data.monthAnchors : []);
      setMetaTotal(typeof data.total === 'number' ? data.total : 0);
      setConfigured(Boolean(data.configured));
      setActiveMonthKey(latestMonthKey(steps));
    } catch {
      setMonthSteps([]);
      setSpecialMonths([]);
      setMonthAnchors([]);
      setMetaTotal(0);
      setConfigured(false);
    } finally {
      setMetaLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      loadMeta(categoryId, searchQuery);
    }, 350);
    return () => clearTimeout(t);
  }, [categoryId, searchQuery, loadMeta]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [categoryId, searchQuery]);

  useEffect(() => {
    if (REMOVED_CATEGORY_IDS.includes(categoryId)) {
      setCategoryId('all');
    }
  }, [categoryId]);

  const jumpToMonth = useCallback(
    async (monthKey) => {
      if (!monthKey || !monthSteps.includes(monthKey)) return;

      const gen = jumpGenRef.current + 1;
      jumpGenRef.current = gen;
      isScrubbingRef.current = true;
      setIsJumping(true);
      setActiveMonthKey(monthKey);

      try {
        const startIndex = findStartIndexForMonth(monthAnchors, monthKey);
        await ensureLoadedThroughIndex(startIndex);
        if (jumpGenRef.current !== gen) return;

        bumpRenderCount(startIndex + GALLERY_RENDER_BATCH + 4);
        await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
        if (jumpGenRef.current !== gen) return;

        scrollToMonthAnchor(monthKey, 'smooth');
      } finally {
        if (jumpGenRef.current === gen) {
          setIsJumping(false);
          window.setTimeout(() => {
            if (jumpGenRef.current === gen) isScrubbingRef.current = false;
          }, 600);
        }
      }
    },
    [monthSteps, monthAnchors, ensureLoadedThroughIndex, bumpRenderCount]
  );

  const handleScrubMonth = useCallback(
    (monthKey) => {
      jumpToMonth(monthKey);
    },
    [jumpToMonth]
  );

  const pageLoading = metaLoading || (loading && items.length === 0);
  const showGrid = !pageLoading && gridRows.length > 0;

  const pumpLoadMore = useCallback(async () => {
    if (isJumpingRef.current) return;

    for (let round = 0; round < PUMP_MAX_ROUNDS; round += 1) {
      const el = sentinelRef.current;
      if (!el || !isSentinelNearViewport(el)) break;

      let waits = 0;
      while ((loadingRef.current || isJumpingRef.current) && waits < 20) {
        waits += 1;
        await new Promise((r) => setTimeout(r, 50));
      }
      if (loadingRef.current || isJumpingRef.current) break;

      const progressed = await loadMoreStep();
      if (!progressed) break;

      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    }
  }, [loadMoreStep]);

  useEffect(() => {
    if (!showGrid) return undefined;

    const el = sentinelRef.current;
    if (!el) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) pumpLoadMore();
      },
      { root: null, rootMargin: `${SENTINEL_MARGIN_PX}px 0px`, threshold: 0 }
    );

    observer.observe(el);
    requestAnimationFrame(() => pumpLoadMore());

    return () => observer.disconnect();
  }, [showGrid, pumpLoadMore, categoryId, searchQuery]);

  useEffect(() => {
    if (!showGrid) return undefined;

    const onScroll = () => {
      const el = sentinelRef.current;
      if (el && isSentinelNearViewport(el)) pumpLoadMore();
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [showGrid, pumpLoadMore]);

  useEffect(() => {
    if (!showGrid || (loading && items.length === 0)) return;
    pumpLoadMore();
  }, [showGrid, renderCount, items.length, loading, loadingMore, pumpLoadMore]);

  useEffect(() => {
    const anchors = document.querySelectorAll('.gallery-month-anchor');
    if (!anchors.length) return undefined;

    const pickActive = () => {
      if (isScrubbingRef.current) return;
      let best = null;
      let bestTop = Infinity;
      anchors.forEach((node) => {
        const rect = node.getBoundingClientRect();
        if (rect.bottom < 0) return;
        if (rect.top <= bestTop) {
          bestTop = rect.top;
          best = node;
        }
      });
      if (!best) return;
      const key = best.getAttribute('data-month-key');
      if (key) setActiveMonthKey(key);
    };

    const observer = new IntersectionObserver(
      () => pickActive(),
      { root: null, rootMargin: '-10% 0px -55% 0px', threshold: [0, 0.01, 0.5, 1] }
    );

    anchors.forEach((node) => observer.observe(node));
    window.addEventListener('scroll', pickActive, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', pickActive);
    };
  }, [gridRows.length, categoryId, searchQuery]);

  const postToLightbox = (item) => {
    const cap = (item.caption || '').trim();
    const short = cap ? (cap.length > 100 ? `${cap.slice(0, 100)}…` : cap) : 'Bejegyzés';
    return {
      id: item.id,
      source: item.source,
      title: short,
      description: cap,
      alt: cap || item.source,
      imageUrl: resolveMediaUrl(item.imageUrl || ''),
      linkUrl: item.linkUrl || '',
    };
  };

  const openLightbox = (item) => {
    setSelectedPost(postToLightbox(item));
    setIsLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setSelectedPost(null);
    setIsLightboxOpen(false);
    document.body.style.overflow = 'unset';
  };

  const navigatePost = (direction) => {
    if (!selectedPost || items.length === 0) return;
    const i = items.findIndex((p) => p.id === selectedPost.id);
    if (i < 0) return;
    const next =
      direction === 'next'
        ? items[(i + 1) % items.length]
        : items[(i - 1 + items.length) % items.length];
    setSelectedPost(postToLightbox(next));
  };

  const trimmedQuery = searchQuery.trim();
  const isSearching = trimmedQuery.length > 0;
  const displayTotal = metaTotal || total;

  useEffect(() => {
    if (!isLightboxOpen || !selectedPost) return;
    if (!items.some((i) => i.id === selectedPost.id)) {
      setSelectedPost(null);
      setIsLightboxOpen(false);
      document.body.style.overflow = 'unset';
    }
  }, [items, selectedPost, isLightboxOpen]);

  const showEmpty = !pageLoading && items.length === 0 && !loading;

  return (
    <div className="gallery-page gallery-page--timetravel">
      <div className="gallery-inner">
        <header className="gallery-hero">
          <div className="gallery-hero-polaroid" aria-hidden="true">
            <img src={polaroidImg} alt="" />
          </div>
          <div className="gallery-hero-text">
            <h1>Időutazás a galériában</h1>
            <p>Görgessen végig az emlékek között, vagy húzza a csúszkát egy időszakhoz.</p>
          </div>
        </header>

        {pageLoading && <p className="gallery-status">Betöltés…</p>}

        {!metaLoading && monthSteps.length > 0 && activeMonthKey && (
          <>
            <GalleryTimeline
              monthSteps={monthSteps}
              specialMonths={specialMonths}
              activeMonthKey={activeMonthKey}
              onScrubMonth={handleScrubMonth}
              isJumping={isJumping}
            />

            <GalleryToolbar
              categoryId={categoryId}
              onCategoryChange={setCategoryId}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              countBeforeSearch={displayTotal}
            />

            {showEmpty && (
              <p className="gallery-status gallery-status--muted">
                {isSearching
                  ? `Nincs találat a „${trimmedQuery}” keresésre.`
                  : 'Nincs ilyen kategóriájú kép. Próbáljon másik szűrőt.'}
              </p>
            )}

            {displayTotal > 0 && (
              <p className="gallery-result-count" role="status">
                <strong>{displayTotal}</strong> kép összesen — görgetés az időben
                {hasMore || canRenderMore ? ' — továbbiak betöltése görgetéskor' : ''}
              </p>
            )}
          </>
        )}

        {!metaLoading && !configured && monthSteps.length === 0 && (
          <p className="gallery-status gallery-status--muted">Még nincs megjeleníthető bejegyzés.</p>
        )}

        {showGrid && (
          <>
            <div className="gallery-posts-grid">
              {gridRows.map((row) => {
                if (row.type === 'anchor') {
                  return (
                    <div
                      key={`anchor-${row.monthKey}`}
                      id={anchorIdForMonth(row.monthKey)}
                      className="gallery-month-anchor"
                      data-month-key={row.monthKey}
                    >
                      <span className="gallery-month-anchor-label">{row.label}</span>
                    </div>
                  );
                }

                const item = row.item;
                const cap = (item.caption || '').trim();
                const srcLabel = socialSourceLabel(item.source);
                return (
                  <button
                    type="button"
                    key={item.id}
                    className="gallery-post-card"
                    title={cap || undefined}
                    onClick={() => openLightbox(item)}
                  >
                    <span className={`gallery-post-badge ${socialSourceBadgeClass(item.source)}`}>
                      {srcLabel}
                    </span>
                    <div className="gallery-post-image-wrap">
                      <img src={resolveMediaUrl(item.imageUrl)} alt="" loading="lazy" decoding="async" />
                    </div>
                  </button>
                );
              })}
            </div>

            <div ref={sentinelRef} className="gallery-scroll-sentinel" aria-hidden="true">
              {(loadingMore || isJumping) && (
                <p className="gallery-status gallery-status--loading-more">
                  {isJumping ? 'Időutazás…' : 'További képek…'}
                </p>
              )}
              {!loadingMore && !isJumping && !hasMore && !canRenderMore && items.length > 0 && (
                <p className="gallery-status gallery-status--muted gallery-end-mark">Minden kép betöltve</p>
              )}
            </div>
          </>
        )}
      </div>

      {isLightboxOpen && selectedPost && (
        <div className="gallery-lightbox-overlay" onClick={closeLightbox} role="presentation">
          <div className="gallery-lightbox-panel" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="gallery-lightbox-close" onClick={closeLightbox} aria-label="Bezárás">
              ✕
            </button>

            {items.length > 1 && (
              <>
                <button
                  type="button"
                  className="gallery-lightbox-nav gallery-lightbox-nav--prev"
                  onClick={() => navigatePost('prev')}
                  aria-label="Előző"
                >
                  ‹
                </button>
                <button
                  type="button"
                  className="gallery-lightbox-nav gallery-lightbox-nav--next"
                  onClick={() => navigatePost('next')}
                  aria-label="Következő"
                >
                  ›
                </button>
              </>
            )}

            <div className="gallery-lightbox-figure">
              <img src={selectedPost.imageUrl} alt={selectedPost.alt || ''} />
            </div>

            <div className="gallery-lightbox-meta">
              <span className="gallery-lightbox-source">{socialSourceLabel(selectedPost.source)}</span>
              {selectedPost.description ? <p>{selectedPost.description}</p> : null}
              {selectedPost.linkUrl ? (
                <a
                  className="gallery-lightbox-link"
                  href={selectedPost.linkUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {selectedPost.source === 'facebook'
                    ? 'Megnyitás Facebookon →'
                    : 'Megnyitás Instagramon →'}
                </a>
              ) : null}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default GalleryPage;
